import { resolveDomainEventOrDecision, resolveSchema } from './workflowSpec.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'
import { sanitizeCardDescription } from './sanitize.js'
import { DomainModelRole } from '../constants/cardTypes.js'

const SCHEMA_LINK_ROLES = [
  DomainModelRole.COMMAND,
  DomainModelRole.AGGREGATE_ROOT,
  DomainModelRole.READ_MODEL
]

const locateEvent = (workflow, domainEvent) => {
  const resolvedEvent = resolveDomainEventOrDecision(domainEvent, workflow)
  if (!resolvedEvent) {
    return { error: `Domain event or decision not found for ref: ${domainEvent}` }
  }

  const domainEvents = workflow.domainEvents || []
  const eventIndex = domainEvents.findIndex((event) => event.id === resolvedEvent.id)
  if (eventIndex === -1) {
    return { error: 'Event not found' }
  }

  return { resolvedEvent, eventIndex, event: domainEvents[eventIndex] }
}

export const buildCreateCardMutation = ({
  workflow,
  workflowId,
  domainEvent,
  description,
  cardTypeId,
  releaseNo,
  done,
  cardTypes
}) => {
  if (!cardTypeId) {
    return { error: 'cardTypeId is required. Use list_card_types to see available card types.' }
  }
  if (!domainEvent) {
    return { error: 'domainEvent is required' }
  }

  const located = locateEvent(workflow, domainEvent)
  if (located.error) {
    return { error: located.error }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))
  if (!cardTypesMap[cardTypeId]) {
    return { error: `Card type "${cardTypeId}" not found. Use list_card_types to see available card types.` }
  }

  const domainEvents = workflow.domainEvents || []
  const sortkey = computeMidString(getLastCardSortkey(domainEvents), '')
  const cardDescription = description || '<p>New card</p>'

  const mutationInput = {
    workflowId,
    expectedVersion: workflow.version,
    updateNote: {
      action: 'added card',
      target: sanitizeCardDescription(cardDescription)
    },
    cards: [{
      description: cardDescription,
      done: done || false,
      cardTypeId,
      releaseNo: releaseNo || null,
      cardIndex: -1,
      sortkey,
      eventId: located.resolvedEvent.id,
      eventIndex: located.eventIndex,
      boundedContext: null,
      schemaId: null
    }]
  }

  return { mutationInput, event: located.resolvedEvent }
}

export const buildUpdateCardMutation = ({
  workflow,
  workflowId,
  domainEvent,
  currentDescription,
  description,
  cardTypeId,
  releaseNo,
  done,
  schema,
  cardTypes
}) => {
  if (!domainEvent) {
    return { error: 'domainEvent is required' }
  }
  if (!currentDescription) {
    return { error: 'currentDescription is required to identify the card' }
  }

  const located = locateEvent(workflow, domainEvent)
  if (located.error) {
    return { error: located.error }
  }

  const sanitizedCurrent = sanitizeCardDescription(currentDescription)
  const cardIndex = (located.event.cards || []).findIndex((card) =>
    sanitizeCardDescription(card.description || '') === sanitizedCurrent
  )
  if (cardIndex === -1) {
    return { error: `Card with description "${currentDescription}" not found on this event` }
  }

  const foundCard = located.event.cards[cardIndex]
  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  if (cardTypeId !== undefined && !cardTypesMap[cardTypeId]) {
    return { error: `Card type "${cardTypeId}" not found. Use list_card_types to see available card types.` }
  }

  let schemaId
  if (schema !== undefined) {
    if (!schema) {
      schemaId = null
    } else {
      const cardType = cardTypesMap[foundCard.cardTypeId]
      if (cardType && !SCHEMA_LINK_ROLES.includes(cardType.domainModelRole)) {
        return { error: `Cannot link a schema to this card. Schemas can only be linked to Command, AggregateRoot, or ReadModel cards. This card's role is: ${cardType.domainModelRole || 'none'}.` }
      }
      const resolvedSchema = resolveSchema(schema, workflow, cardTypesMap)
      if (!resolvedSchema) {
        return { error: `Schema not found for ref: ${schema}` }
      }
      schemaId = resolvedSchema.id
    }
  }

  const changes = []
  if (description !== undefined) {
    changes.push('description updated')
  }
  if (cardTypeId !== undefined) {
    changes.push(`card type changed to "${cardTypesMap[cardTypeId]?.title || cardTypeId}"`)
  }
  if (releaseNo !== undefined) {
    changes.push(releaseNo ? `assigned to release ${releaseNo}` : 'release cleared')
  }
  if (done !== undefined) {
    changes.push(done ? 'marked as done' : 'marked as not done')
  }
  if (schemaId !== undefined) {
    changes.push(schemaId ? 'schema linked' : 'schema unlinked')
  }

  if (changes.length === 0) {
    return { error: 'No changes specified' }
  }

  const cardInput = {
    cardId: foundCard.id,
    cardIndex,
    eventId: located.resolvedEvent.id,
    eventIndex: located.eventIndex,
    expectedVersion: foundCard.version || undefined,
    description: description !== undefined ? description : foundCard.description,
    done: done !== undefined ? done : foundCard.done,
    cardTypeId: cardTypeId !== undefined ? cardTypeId : foundCard.cardTypeId,
    releaseNo: releaseNo !== undefined ? releaseNo : foundCard.releaseNo,
    sortkey: foundCard.sortkey,
    boundedContext: foundCard.boundedContext,
    schemaId: schemaId !== undefined ? schemaId : foundCard.schemaId
  }

  const mutationInput = {
    workflowId,
    expectedVersion: workflow.version,
    updateNote: {
      action: 'updated card',
      target: sanitizeCardDescription(description || foundCard.description || '')
    },
    cards: [cardInput]
  }

  return { mutationInput, card: foundCard, changes }
}

export const buildDeleteCardMutation = ({ workflow, workflowId, domainEvent, description }) => {
  if (!domainEvent) {
    return { error: 'domainEvent is required' }
  }
  if (!description) {
    return { error: 'description is required to identify the card' }
  }

  const located = locateEvent(workflow, domainEvent)
  if (located.error) {
    return { error: located.error }
  }

  const sanitizedDesc = sanitizeCardDescription(description)
  const cardIndex = (located.event.cards || []).findIndex((card) =>
    sanitizeCardDescription(card.description || '') === sanitizedDesc
  )
  if (cardIndex === -1) {
    return { error: `Card with description "${description}" not found on this event` }
  }

  const foundCard = located.event.cards[cardIndex]

  const deleteInput = {
    workflowId,
    eventId: located.resolvedEvent.id,
    eventIndex: located.eventIndex,
    cardId: foundCard.id,
    cardIndex,
    expectedVersion: workflow.version,
    cardDescription: sanitizeCardDescription(foundCard.description || '')
  }

  return { deleteInput, card: foundCard, event: located.resolvedEvent }
}
